import { useMemo, useState } from "react";
import { useLoaderData, useNavigate, useNavigation, useSubmit } from "react-router-dom";

import { useAuthStore } from "src/components/main/authStore";
import {
    buildWeekNumArray,
    getDateEndFromWeekNum,
    getDateEndFromWeekNum2,
    getDateTime,
    getDatefromWeekNum,
    MyDate,
    outsideNotWorkingReasons,
    outsideWorkingOns,
    standbyStartDate,
    weekDays,
    weeknum,
} from "src/components/common/functions";

type DailyCallOutDay = {
    acknowledged: boolean;
    afternoon: boolean;
    commuteOrHotel: string;
    division: string;
    doOT: string;
    morning: boolean;
    notWorkingReason: string;
    otDate: string;
    otTime: string;
    unlock: boolean;
    working: string;
    workingOn: string;
};

type DailyCallOutFormData = {
    form: DailyCallOutDay[];
    status?: string;
};

type DivisionOption = {
    division: string;
};

type LoaderData = {
    divisions: DivisionOption[];
    form: DailyCallOutFormData;
    week: string;
};

const title = "DailyCallOut";
const emptyDay = (): DailyCallOutDay => ({
    acknowledged: false,
    afternoon: false,
    commuteOrHotel: "",
    division: "",
    doOT: "",
    morning: false,
    notWorkingReason: "",
    otDate: "",
    otTime: "",
    unlock: false,
    working: "",
    workingOn: "",
});

export default function DailyCallOut() {
    const { week, form: loadedForm, divisions } = useLoaderData() as LoaderData;
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    const navigation = useNavigation();
    const submit = useSubmit();
    const [form, setForm] = useState<DailyCallOutFormData>(loadedForm);
    const [isEditingContent, setIsEditingContent] = useState(false);
    const isSubmitting = navigation.state === "submitting";
    const hireDateString = user?.hireDate ?? "";
    const quitDateString = user?.quitDate ?? "";

    const weeks = useMemo(
        () => buildWeekNumArray(standbyStartDate).filter((value) => (
            getDateTime(value, 7, 0, 0) > new Date(hireDateString.replace(/-/g, "/")) &&
            (quitDateString ? getDateTime(value, 0, 0, 0) <= new Date(quitDateString.replace(/-/g, "/")) : true)
        )).reverse(),
        [hireDateString, quitDateString]
    );

    const activeWeek = weeks.includes(week)
        ? week
        : (weeks.includes(weeknum(new MyDate().s())) ? weeknum(new MyDate().s()) : (weeks[0] || ""));

    const timeValid = (weekValue: string, index: number) => getDateTime(weekValue, index, 7, 30) > new Date();
    const activeWeekEnd = getDateEndFromWeekNum2(activeWeek);
    const canEdit = activeWeekEnd ? new Date(activeWeekEnd) >= new Date() : false;
    const currentStatus = form.status || "Created";
    const hasUnlock = form.form.some(day => day.unlock);
    const canOpenEdit = (currentStatus !== "Submitted" && canEdit) || hasUnlock;

    const updateDay = (dayIndex: number, updater: (day: DailyCallOutDay) => DailyCallOutDay) => {
        setForm((currentForm) => ({
            ...currentForm,
            form: currentForm.form.map((day, index) => index === dayIndex ? updater(day) : day),
        }));
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-end flex-wrap gap-3">
                <div>
                    <label className = "small text-muted fw-bold mb-1" htmlFor = "dailyCallOutWeek">Week</label>
                    <select
                        id = "dailyCallOutWeek"
                        className = "form-control w-auto"
                        value = {activeWeek}
                        onChange = {e => {
                            setIsEditingContent(false);
                            navigate(`/${title}/${e.currentTarget.value}`);
                        }}
                    >
                        {weeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </div>
            </nav>
            <form
                className = "p-3"
                onSubmit = {e => {
                    e.preventDefault();
                    submit(new FormData(e.currentTarget), { method: "post" });
                    setIsEditingContent(false);
                }}
            >
                <input type = "hidden" name = "currentWeek" value = {activeWeek} />
                <input type = "hidden" name = "data" value = {JSON.stringify(form)} />
                <div className = "h3 text-danger fw-bold">{getDateEndFromWeekNum(activeWeek)}</div>
                <div className = "h5 fw-bold mb-3" style = {{ color: currentStatus === "Submitted" ? "#7c3aed" : currentStatus === "Saved" ? "#ea580c" : "#475569" }}>
                    {currentStatus}
                </div>
                <div className = "h5 fw-bold">Job Hazard Analysis Check</div>
                <li>
                    Error-likely with job site
                    <ul>
                        <li>Unfamiliarity with job site</li>
                        <li>Distractions / interruptions</li>
                    </ul>
                </li>
                <li>
                    Pre Job Brief
                    <ul>
                        <li>Did I identity all hazards?</li>
                        <li>Do I need assistance?</li>
                        <li>How do I prepare for what cound go wrong?</li>
                    </ul>
                </li>
                <li>
                    Routine Activities
                    <ul>
                        <li>Avoid "auto-pilot"</li>
                        <li>Stay engaged in the task at hand</li>
                    </ul>
                </li>
                <li>
                    Questioning Attitude
                    <ul>
                        <li>Stop, look and listen</li>
                        <li>Do not make assumptions</li>
                    </ul>
                </li>
                <li>
                    Weather Conditions
                    <ul>
                        <li>Did I identity weather hazards</li>
                    </ul>
                </li>
                <li>360 Vehicle Inspection</li>
                <div className = "row">
                    {weekDays.map((weekday, index) => (
                        <div className = "col-lg" key = {index}>
                            <div className = "mt-2 h4 text-secondary">{weekday} ({getDatefromWeekNum(activeWeek, index)})</div>
                            {!timeValid(activeWeek, index) && !form.form[index].unlock ? (
                                <div className = "text-center"><span className = "badge badge-primary">past</span></div>
                            ) : null}
                            <div className = "mt-2">
                                <input
                                    id = {`acknowledge-${index}`}
                                    className = "cssCheckBox mt-2"
                                    type = "checkbox"
                                    disabled = {!(isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock))}
                                    checked = {form.form[index].acknowledged}
                                    onChange = {e => {
                                        const checked = e.currentTarget.checked;
                                        updateDay(index, (day) => checked ? { ...day, acknowledged: checked } : emptyDay());
                                    }}
                                />
                                <label htmlFor = {`acknowledge-${index}`} className = "ms-2">I acknowledge that I understand and will apply the above JHA to every ticket.</label>
                            </div>
                            {form.form[index].acknowledged ? (
                                <>
                                    {form.form[index].working !== "" || (isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock)) ? (
                                        <label htmlFor = {`working-${index}`} className = "fw-bold mt-2 mb-0">Working:</label>
                                    ) : null}
                                    {isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock) ? (
                                        <select
                                            id = {`working-${index}`}
                                            className = "form-control"
                                            value = {form.form[index].working}
                                            onChange = {e => {
                                                const value = e.currentTarget.value;
                                                updateDay(index, (day) => ({ ...day, working: value }));
                                            }}
                                        >
                                            <option value = "">Select</option>
                                            <option>Yes</option>
                                            <option>No</option>
                                        </select>
                                    ) : <span className = "ms-2">{form.form[index].working}</span>}
                                    {form.form[index].working === "Yes" ? (
                                        <>
                                            <div className = "mt-2">
                                                <label htmlFor = {`commuteOrHotel-${index}`} className = "fw-bold">Commute or Hotel:</label>
                                                {isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock) ? (
                                                    <div>
                                                        <select
                                                            id = {`commuteOrHotel-${index}`}
                                                            className = "form-control"
                                                            required
                                                            value = {form.form[index].commuteOrHotel}
                                                            onChange = {e => {
                                                                const value = e.currentTarget.value;
                                                                updateDay(index, (day) => ({ ...day, commuteOrHotel: value }));
                                                            }}
                                                        >
                                                            <option value = "">Select</option>
                                                            <option>Commute</option>
                                                            <option>Hotel</option>
                                                        </select>
                                                    </div>
                                                ) : <span className = "ms-2">{form.form[index].commuteOrHotel}</span>}
                                            </div>
                                            <div className = "mt-2">
                                                <label htmlFor = {`division-${index}`} className = "fw-bold">Division:</label>
                                                {isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock) ? (
                                                    <select
                                                        id = {`division-${index}`}
                                                        className = "form-control"
                                                        required
                                                        value = {form.form[index].division}
                                                        onChange = {e => {
                                                            const value = e.currentTarget.value;
                                                            updateDay(index, (day) => ({ ...day, division: value }));
                                                        }}
                                                    >
                                                        <option value = "">Select</option>
                                                        {divisions.map((division, optionIndex) => <option key = {optionIndex}>{division.division}</option>)}
                                                    </select>
                                                ) : <span className = "ms-2">{form.form[index].division}</span>}
                                            </div>
                                            <div className = "mt-2">
                                                <label htmlFor = {`workingOn-${index}`} className = "fw-bold">Working On:</label>
                                                {isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock) ? (
                                                    <select
                                                        id = {`workingOn-${index}`}
                                                        className = "form-control"
                                                        required
                                                        value = {form.form[index].workingOn}
                                                        onChange = {e => {
                                                            const value = e.currentTarget.value;
                                                            updateDay(index, (day) => ({ ...day, workingOn: value }));
                                                        }}
                                                    >
                                                        <option value = "">Select</option>
                                                        {outsideWorkingOns.map((workingOn, optionIndex) => <option key = {optionIndex}>{workingOn}</option>)}
                                                    </select>
                                                ) : <span className = "ms-2">{form.form[index].workingOn}</span>}
                                            </div>
                                        </>
                                    ) : null}
                                    {form.form[index].working === "No" ? (
                                        <div className = "mt-2">
                                            <label htmlFor = {`reason-${index}`} className = "fw-bold">Reason:</label>
                                            {isEditingContent && (timeValid(activeWeek, index) || form.form[index].unlock) ? (
                                                <div>
                                                    <select
                                                        id = {`reason-${index}`}
                                                        className = "form-control"
                                                        required
                                                        value = {form.form[index].notWorkingReason}
                                                        onChange = {e => {
                                                            const value = e.currentTarget.value;
                                                            updateDay(index, (day) => ({ ...day, notWorkingReason: value }));
                                                        }}
                                                    >
                                                        <option value = "">Select</option>
                                                        {outsideNotWorkingReasons.map((reason, optionIndex) => <option key = {optionIndex}>{reason}</option>)}
                                                    </select>
                                                </div>
                                            ) : <span className = "ms-2">{form.form[index].notWorkingReason}</span>}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    ))}
                </div>
                {canOpenEdit ? (
                    <div className = "mt-2 text-right">
                        {isEditingContent ? (
                            <>
                                <button
                                    key = "save"
                                    type = "button"
                                    className = "btn btn-primary me-3"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        setForm(current => ({ ...current, status: "Saved" }));
                                        const formData = new FormData();
                                        formData.append("currentWeek", activeWeek);
                                        formData.append("data", JSON.stringify({ ...form, status: "Saved" }));
                                        submit(formData, { method: "post" });
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Saving..." : "Save Content"}
                                </button>
                                <button
                                    key = "submit"
                                    type = "button"
                                    className = "btn btn-dark"
                                    disabled = {isSubmitting}
                                    onClick = {() => {
                                        if (!window.confirm("Are you sure to submit for the week?")) return;
                                        setForm(current => ({ ...current, status: "Submitted" }));
                                        const formData = new FormData();
                                        formData.append("currentWeek", activeWeek);
                                        formData.append("data", JSON.stringify({ ...form, status: "Submitted" }));
                                        submit(formData, { method: "post" });
                                        setIsEditingContent(false);
                                    }}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit For the Week"}
                                </button>
                            </>
                        ) : (
                            <button key = "edit" type = "button" className = "btn btn-primary" onClick = {() => {
                                setIsEditingContent(true);
                            }}>
                                Edit Content
                            </button>
                        )}
                    </div>
                ) : null}
            </form>
        </div>
    );
}
